/**
 * The shed film's soundtrack: foley under every knock the picture shows, and a light
 * marimba under the whole of it from the moment the designer opens.
 *
 * Every cue is read off the timing the picture is drawn from (`sheds.ts`, `shed.ts`) —
 * the hammer lands on the frame the star appears, a joist knocks as its bounce touches
 * the runners, the checklist ticks as the pen does — so a change to the picture's timing
 * moves its sound with it. `test/film.test.ts` holds the hammer to the star.
 *
 * The music is in F, at a tempo chosen so that twelve bars run from the designer to the
 * end card exactly: the last chord lands as the title block does, and rings out.
 */

import { designSound } from "./designer.js";
import { driveSound, shopSound } from "./errands.js";
import type { Site } from "./film.js";
import { wordsSound } from "./props.js";
import { TEN_BY_TWELVE, landings } from "./shed.js";
import {
  AT,
  AVALANCHE,
  DESIGN,
  DONE,
  GARAGE,
  PLAN,
  SHOPPING,
  SPOTS,
  STEPS,
  WIPES,
  captions,
  designState,
  hammerBlows,
  junkLands,
} from "./sheds.js";
import type { Mix } from "./sound.js";
import { raisingSound } from "./yard.js";

/** Stage x to stereo: the left of the square to the right of it, never hard to one side. */
function panAt(x: number): number {
  return Math.max(-0.7, Math.min(0.7, x / 640));
}

export function shedScore(mix: Mix, site: Site): void {
  garage(mix, AT.garage);
  design(mix, AT.design);
  shopSound(mix, AT.shop, SHOPPING, [77, 79, 81, 84, 86]);
  driveSound(mix, AT.drive, AT.build - AT.drive);
  build(mix, AT.build);
  done(mix, AT.done);
  wordsSound(mix, captions(site), WIPES);
  music(mix);
}

// ---- the garage -------------------------------------------------------------------------

function garage(mix: Mix, at: number): void {
  const [opens, opened] = GARAGE.door;
  mix.rattle(at + opens, opened - opens, { gain: 0.8 });
  mix.clank(at + opened, { gain: 0.45, base: 300 });
  mix.whoosh(at + GARAGE.avalanche.start - 0.05, 0.35, { from: 300, to: 1600, gain: 0.55 });

  AVALANCHE.forEach((flight, i) => {
    const lands = at + junkLands(i);
    const pan = panAt(flight.to.x);
    switch (flight.kind) {
      case "box":
        mix.thud(lands, { gain: 0.7, pan, weight: 1.1 });
        mix.slap(lands, { gain: 0.25, pan });
        break;
      case "tire":
        mix.boing(lands, { gain: 0.45, pan, pitch: 0.8 });
        mix.thud(lands, { gain: 0.35, pan, weight: 1.3 });
        break;
      case "bike":
        mix.clank(lands, { gain: 0.5, pan, base: 640 });
        mix.thud(lands, { gain: 0.25, pan });
        break;
      case "can":
        mix.clank(lands, { gain: 0.4, pan, base: 910 });
        break;
      case "ball":
        mix.boing(lands, { gain: 0.45, pan, pitch: 1.3 });
        mix.boing(lands + 0.22, { gain: 0.2, pan: pan - 0.1, pitch: 1.4 });
        break;
      case "kayak":
        mix.thud(lands, { gain: 0.6, pan, weight: 0.8 });
        break;
      case "rake":
        mix.tick(lands, { gain: 0.8, pan, pitch: 0.6 });
        mix.clank(lands, { gain: 0.12, pan, base: 1500 });
        break;
      case "lamp":
        mix.clank(lands, { gain: 0.25, pan, base: 1200 });
        break;
      case "mower":
        mix.thud(lands, { gain: 0.85, pan, weight: 1.4 });
        mix.clank(lands, { gain: 0.25, pan, base: 380 });
        break;
    }
  });

  // Out of the top of the pile; a thought, bubble by bubble; the shed it dreams of.
  mix.pop(at + GARAGE.pop + 0.02, { gain: 0.8 });
  const [thinks, thought] = GARAGE.think;
  [0, 0.12, 0.24].forEach((k, i) => mix.pop(at + thinks + k * (thought - thinks), { gain: 0.45, pitch: 1.2 + i * 0.25, pan: 0.15 }));
  mix.pop(at + thinks + 0.36 * (thought - thinks), { gain: 0.5, pitch: 0.9, pan: 0.25 });
  const [dreams, dreamt] = GARAGE.vision;
  const rising = [65, 69, 72, 77, 81, 84];
  rising.forEach((note, i) => mix.glock(at + dreams + (i / rising.length) * (dreamt - dreams), note, 0.45 + i * 0.06, 0.25));
  mix.glock(at + dreamt, 89, 0.55, 0.25);
  const [zooms, zoomed] = GARAGE.zoom;
  mix.whoosh(at + zooms, zoomed - zooms, { from: 250, to: 3600, gain: 0.6, pan: 0.2 });
}

// ---- designing and buying ------------------------------------------------------------------

function design(mix: Mix, at: number): void {
  const pan = 0.1;
  const presses = [DESIGN.widthDrag[0], DESIGN.lengthDrag[0], ...DESIGN.roofClicks, DESIGN.doorClick];
  for (const press of presses) {
    mix.click(at + press, { gain: 0.9, pan });
  }
  for (const release of [DESIGN.widthDrag[1], DESIGN.lengthDrag[1]]) {
    mix.click(at + release, { gain: 0.5, pan });
  }
  // A detent at every foot the sliders pass.
  let width = Math.floor(designState(0).width);
  let length = Math.floor(designState(0).length);
  for (let u = 0; u < DESIGN.doorClick; u += 0.002) {
    const state = designState(u);
    if (Math.floor(state.width) !== width || Math.floor(state.length) !== length) {
      mix.tick(at + u, { gain: 0.3, pitch: 1.5, pan });
      width = Math.floor(state.width);
      length = Math.floor(state.length);
    }
  }
  for (const choice of DESIGN.roofClicks) {
    mix.whoosh(at + choice, 0.22, { from: 600, to: 1800, gain: 0.18, pan });
  }
  designSound(mix, at, DESIGN, pan);
}

// ---- the build -------------------------------------------------------------------------------

function build(mix: Mix, at: number): void {
  const pieces = landings(TEN_BY_TWELVE);
  const when = (stage: readonly [number, number] | readonly [number, number, number], p: number): number =>
    at + stage[0] + p * (stage[1] - stage[0]);

  // The guide's page turns at every step.
  for (const step of STEPS.slice(1)) {
    mix.whoosh(at + step.at, 0.14, { from: 2400, to: 5200, gain: 0.16, pan: -0.6 });
  }
  // A puff of dust each time the time-lapse moves the builder.
  for (const spot of SPOTS.slice(1, -1)) {
    mix.whoosh(at + spot, 0.16, { from: 500, to: 1400, gain: 0.12 });
  }

  pieces.runners.forEach((p) => mix.thud(when(PLAN.runners, p), { gain: 0.7, weight: 1.15, pan: -0.05 }));
  pieces.joists.forEach((p, i) => {
    mix.thud(when(PLAN.joists, p), { gain: 0.35, weight: 1.4, pan: -0.05 });
    mix.tick(when(PLAN.joists, p), { gain: 0.32, pitch: 0.7 + (i % 3) * 0.04 });
  });
  pieces.deck.forEach((p) => mix.slap(when(PLAN.deck, p), { gain: 0.8, pan: -0.05 }));

  raisingSound(mix, at, TEN_BY_TWELVE, PLAN, pieces, [84, 89]);

  hammerBlows().forEach((blow, i) => mix.tap(at + blow.at, { gain: 0.72, pan: blow.pan, pitch: 0.95 + ((i * 7) % 5) * 0.025 }));
}

function done(mix: Mix, at: number): void {
  const [approaches] = DONE.approach;
  const [, inside] = DONE.inward;
  mix.roll(at + approaches, inside - approaches, { gain: 0.55, pan: -0.25 });
  mix.whoosh(at + DONE.open[0], DONE.open[1] - DONE.open[0], { from: 300, to: 800, gain: 0.18, pan: -0.1 });
  mix.thud(at + inside, { gain: 0.4, weight: 1.2, pan: -0.05 });
  mix.whoosh(at + DONE.close[0], DONE.close[1] - DONE.close[0], { from: 300, to: 700, gain: 0.16, pan: -0.1 });
  mix.click(at + DONE.close[1], { gain: 0.6, pan: -0.1 });
  mix.thud(at + DONE.close[1], { gain: 0.45, weight: 0.85, pan: -0.1 });

  // The bird: wings on the way in, a song on the ridge.
  mix.flutter(at + DONE.bird[0], DONE.bird[1] - DONE.bird[0], { gain: 0.22, pan: 0.35 });
  mix.chirp(at + DONE.song[0], { gain: 0.8, pan: 0.05 });
  mix.chirp(at + DONE.song[0] + 0.38, { gain: 0.65, pan: 0.05 });
  mix.glock(at + DONE.glints[0][0] + 0.1, 96, 0.28, 0.3);
  mix.glock(at + DONE.glints[1][0] + 0.1, 100, 0.25, -0.1);

  // The title block slides up and settles.
  const [rises] = DONE.card;
  mix.whoosh(at + rises, 0.42, { from: 200, to: 1300, gain: 0.45 });
  mix.thud(at + rises + 0.21, { gain: 0.35, weight: 1.3 });
}

// ---- the music ---------------------------------------------------------------------------------

interface Chord {
  bass: number;
  tones: readonly [number, number, number, number];
}

const F: Chord = { bass: 53, tones: [65, 69, 72, 77] };
const C: Chord = { bass: 48, tones: [64, 67, 72, 76] };
const DM: Chord = { bass: 50, tones: [65, 69, 74, 77] };
const BB: Chord = { bass: 46, tones: [62, 65, 70, 74] };

/** Twelve bars, and a thirteenth chord to end on. */
const BARS: readonly Chord[] = [F, C, DM, BB, F, C, BB, C, F, C, DM, BB];

/** The order the arpeggio walks a chord's four tones in, eight to a bar. */
const WALK = [0, 2, 1, 3, 2, 1, 3, 2] as const;

/** When the music starts and when its last chord lands: the designer, and the end card. */
export const MUSIC = { start: AT.design, end: AT.design + 23 } as const;

function music(mix: Mix): void {
  const bar = (MUSIC.end - MUSIC.start) / BARS.length;
  const beat = bar / 4;
  const eighth = beat / 2;
  BARS.forEach((chord, b) => {
    const t0 = MUSIC.start + b * bar;
    const building = t0 >= AT.build - beat;
    mix.mallet(t0, chord.bass, 0.85, { ring: 1.6 });
    mix.mallet(t0 + 2 * beat, chord.bass + 7, 0.55, { ring: 1.3 });
    WALK.forEach((tone, e) => {
      const velocity = e === 0 ? 0.62 : 0.38 + 0.08 * ((e * 5 + b * 3) % 4);
      mix.mallet(t0 + e * eighth, chord.tones[tone], velocity, { pan: e % 2 === 0 ? -0.22 : 0.22 });
      if (e % 2 === 1 || building) {
        mix.shaker(t0 + e * eighth, e % 2 === 1 ? 0.55 : 0.3);
      }
    });
    if (building) {
      mix.kick(t0, 0.55);
      mix.kick(t0 + 2 * beat, 0.45);
    }
  });
  // The last chord, rolled from the bottom up, with a bell on top: it rings to the end.
  [41, 53, 60, 65, 69, 72, 77].forEach((note, i) => mix.mallet(MUSIC.end + i * 0.028, note, 0.72 - i * 0.03, { ring: 2.6 }));
  mix.glock(MUSIC.end + 0.06, 84, 0.45);
  mix.glock(MUSIC.end + 0.12, 89, 0.4);
}
